import { Box, Chip, Typography } from "@mui/joy";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

// ─── Цвета единой системы ─────────────────────────────────────────────────
export const COLORS = {
  dark: "#0B0D1A",
  darkMid: "#141830",
  darkAccent: "#1a1f3a",
  brand: "#6366f1",
  brand2: "#8b5cf6",
  brandLight: "#a5b4fc",
  bgLight: "#F7F6FD",
  border: "rgba(255,255,255,0.08)",
  textDim: "rgba(255,255,255,0.65)",
  textMute: "rgba(255,255,255,0.4)",
};

// ─── Секция-обёртка с якорем и анимацией появления ───────────────────────
export function Section({ id, dark = false, children, sx = {}, py = { xs: 8, md: 12 } }) {
  return (
    <Box
      component="section"
      id={id}
      sx={{
        position: "relative",
        py,
        px: { xs: 3, md: 4 },
        background: dark ? COLORS.dark : COLORS.bgLight,
        color: dark ? "#fff" : "inherit",
        overflow: "hidden",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Контейнер фиксированной ширины ───────────────────────────────────────
export function Container({ children, maxWidth = 1180, sx = {} }) {
  return (
    <Box sx={{ maxWidth, mx: "auto", position: "relative", ...sx }}>
      {children}
    </Box>
  );
}

// ─── Chip-метка над заголовками секций ────────────────────────────────────
export function SectionEyebrow({ children, dark = false }) {
  return (
    <Chip
      variant="outlined"
      size="sm"
      sx={{
        mb: 2,
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        borderColor: dark ? "rgba(99,102,241,0.4)" : "rgba(99,102,241,0.25)",
        background: dark ? "rgba(99,102,241,0.12)" : "rgba(99,102,241,0.08)",
        color: dark ? COLORS.brandLight : COLORS.brand,
        "--Chip-paddingInline": "14px",
      }}
    >
      {children}
    </Chip>
  );
}

// ─── Заголовок секции (H2) ────────────────────────────────────────────────
export function SectionTitle({ children, dark = false, sx = {} }) {
  return (
    <Typography
      level="h2"
      sx={{
        fontWeight: 800,
        letterSpacing: "-1.5px",
        lineHeight: 1.1,
        fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
        color: dark ? "#fff" : COLORS.dark,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

// ─── Подзаголовок секции ──────────────────────────────────────────────────
export function SectionSubtitle({ children, dark = false, sx = {} }) {
  return (
    <Typography
      level="body-lg"
      sx={{
        mt: 2,
        maxWidth: 640,
        lineHeight: 1.6,
        color: dark ? COLORS.textDim : "neutral.600",
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

// ─── Заголовочная группа секции (eyebrow + title + subtitle) ──────────────
export function SectionHeader({ eyebrow, title, subtitle, align = "center", dark = false, sx = {} }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <Box
      ref={ref}
      sx={{
        textAlign: align,
        mb: { xs: 5, md: 7 },
        display: "flex",
        flexDirection: "column",
        alignItems: align === "center" ? "center" : "flex-start",
        ...sx,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        {eyebrow && <SectionEyebrow dark={dark}>{eyebrow}</SectionEyebrow>}
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: 0.08 }}
      >
        <SectionTitle dark={dark}>{title}</SectionTitle>
      </motion.div>
      {subtitle && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.15 }}
          style={{ width: "100%", display: "flex", justifyContent: align === "center" ? "center" : "flex-start" }}
        >
          <SectionSubtitle dark={dark}>{subtitle}</SectionSubtitle>
        </motion.div>
      )}
    </Box>
  );
}

// ─── Фоновая «миллиметровка» для светлых секций ───────────────────────────
export function BlueprintGrid({ opacity = 0.04, size = 32 }) {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        backgroundImage: `
          linear-gradient(to right, rgba(99,102,241,${opacity}) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(99,102,241,${opacity}) 1px, transparent 1px)
        `,
        backgroundSize: `${size}px ${size}px`,
        maskImage: "radial-gradient(ellipse at center, #000 40%, transparent 100%)",
      }}
    />
  );
}

// ─── Светящиеся «орбы» на тёмных секциях ──────────────────────────────────
export function GlowOrbs() {
  return (
    <>
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
        transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }}
        style={{
          position: "absolute",
          top: -120, right: -120,
          width: 380, height: 380,
          background: "rgba(99,102,241,0.22)",
          borderRadius: "50%",
          filter: "blur(70px)",
          pointerEvents: "none",
        }}
      />
      <motion.div
        animate={{ scale: [1, 1.12, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ repeat: Infinity, duration: 9, delay: 1.5, ease: "easeInOut" }}
        style={{
          position: "absolute",
          bottom: -80, left: -80,
          width: 300, height: 300,
          background: "rgba(139,92,246,0.2)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />
    </>
  );
}

// ─── Форматирование цены ──────────────────────────────────────────────────
export const formatPrice = (n) => n.toLocaleString("ru-RU") + " ₽";